// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 10-security-compliance.md
// @greenlight-spec: 41-groups-management.md
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import { usePageTitle } from '@/components/app-shell/AppShell';
import { EmptyState } from '@/components/greenlight/EmptyState';
import { PageHeader } from '@/components/greenlight/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GroupsSection } from '@/components/users/GroupsSection';
import { RbacMatrix } from '@/components/users/RbacMatrix';
import { UserInspector } from '@/components/users/UserInspector';
import { UsersSection } from '@/components/users/UsersSection';
import { FilterPill } from '@/components/greenlight/FilterPill';
import {
  applyUserFilters,
  parseUserStatus,
  splitUserSections,
  userStatusCounts,
  type UserStatusFilter,
} from '@/components/users/users-logic';
import { api } from '@/lib/api';
import { useCurrentUser } from '@/lib/auth';
import { deriveListQuery } from '@/lib/listQuery';
import type { UserListItem } from '@greenlight/shared';

const PAGE_SIZE = 100;

// ---------------------------------------------------------------------------
// RBAC-gated page entry point
// ---------------------------------------------------------------------------

export function UsersPage(): React.JSX.Element {
  usePageTitle('Users & RBAC');
  const user = useCurrentUser();
  const isOrgAdmin = user?.role === 'org_admin';
  const isAdmin = user?.role === 'admin';

  // An `admin` reaches this surface only in Greenlight-managed mode (docs/28
  // §/users), so their access depends on the org's role_source. `org_admin`
  // always has access and needn't wait on the settings fetch.
  const { data, isLoading } = api.settings.get.useQuery(['settings-users'], undefined, {
    queryKey: ['settings'],
    enabled: isAdmin,
  });
  const roleSource = data?.status === 200 ? data.body.role_source : undefined;

  const restricted = (
    <div className="space-y-6" data-testid="users-page">
      <PageHeader eyebrow="Access control" title="Users & RBAC" />
      <EmptyState
        title="Access restricted"
        description="User management is available to org admins."
      />
    </div>
  );

  if (isOrgAdmin) return <UsersPageView />;
  if (isAdmin) {
    if (isLoading) {
      return (
        <div className="space-y-6" data-testid="users-page">
          <PageHeader eyebrow="Access control" title="Users & RBAC" />
          <div className="h-16 w-full rounded-gl bg-muted motion-safe:animate-pulse" />
        </div>
      );
    }
    if (roleSource === 'greenlight') return <UsersPageView />;
  }
  return restricted;
}

// ---------------------------------------------------------------------------
// Data + filter view
// ---------------------------------------------------------------------------

const STATUS_PILLS: Array<{ value: UserStatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'deactivated', label: 'Deactivated' },
];

function UsersPageView(): React.JSX.Element {
  const currentUser = useCurrentUser();
  const isOrgAdmin = currentUser?.role === 'org_admin';
  const [searchParams, setSearchParams] = useSearchParams();
  // `selectedUser` holds the row being inspected; `inspectOpen` drives the panel's
  // visibility separately so the inspector animates closed (content still mounted)
  // rather than unmounting the instant the selection clears.
  const [selectedUser, setSelectedUser] = React.useState<UserListItem | null>(null);
  const [inspectOpen, setInspectOpen] = React.useState(false);

  const openInspect = (user: UserListItem): void => {
    setSelectedUser(user);
    setInspectOpen(true);
  };

  // URL-driven filter state
  const status = parseUserStatus(searchParams.get('status'));
  const q = searchParams.get('q') ?? '';

  function setStatus(next: UserStatusFilter): void {
    const p = new URLSearchParams(searchParams);
    if (next === 'all') {
      p.delete('status');
    } else {
      p.set('status', next);
    }
    setSearchParams(p, { replace: true });
  }

  function setQuery(next: string): void {
    const p = new URLSearchParams(searchParams);
    if (!next) {
      p.delete('q');
    } else {
      p.set('q', next);
    }
    setSearchParams(p, { replace: true });
  }

  // Data — one page of up to 100 users at a time, cursor-paginated (search
  // and status filters below apply within the loaded page, matching /apps).
  const cursor = searchParams.get('cursor') ?? undefined;
  const [cursorStack, setCursorStack] = React.useState<string[]>([]);
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = api.users.list.useQuery(['users', cursor ?? null], {
    query: { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) },
  });
  const { items, nextCursor, hasError } = deriveListQuery<UserListItem>({ data, isError });
  // Counts, pills, and filters below all derive from `items` — accurate for
  // the common (single-page) org, but page-scoped once there's more than one
  // page, so the header must say so rather than read as an org-wide total.
  // Keyed off the URL `cursor`, not `cursorStack`: a deep link or refresh onto
  // a non-first page (including the last one, where `nextCursor` is null) has
  // an empty stack but is still viewing one page of a larger roster.
  const isPaginated = cursor !== undefined || nextCursor !== null;

  function goNext(): void {
    if (!nextCursor) return;
    setCursorStack((s) => [...s, cursor ?? '']);
    const p = new URLSearchParams(searchParams);
    p.set('cursor', nextCursor);
    setSearchParams(p, { replace: true });
  }

  function goPrev(): void {
    if (cursorStack.length === 0) return;
    const prev = cursorStack[cursorStack.length - 1];
    setCursorStack((s) => s.slice(0, -1));
    const p = new URLSearchParams(searchParams);
    if (prev) p.set('cursor', prev);
    else p.delete('cursor');
    setSearchParams(p, { replace: true });
  }

  // The org's role-management mode (docs/10) decides whether roles are editable
  // here (greenlight) and whether the group→role mapping editor renders (idp).
  const { data: settingsData } = api.settings.get.useQuery(['settings-users'], undefined, {
    queryKey: ['settings'],
  });
  const roleSource = settingsData?.status === 200 ? settingsData.body.role_source : undefined;
  // A separate axis from roleSource (docs/41): an Entra org with Greenlight-managed
  // roles still sources groups from its IdP, and a Google org is the reverse.
  const groupSource = settingsData?.status === 200 ? settingsData.body.group_source : undefined;

  // Derived view data
  const filtered = applyUserFilters(items, status, q);
  const { admins, members, deactivated } = splitUserSections(filtered);
  const counts = userStatusCounts(items);

  // Summary counts (computed from full unfiltered list)
  const activeCount = items.filter((u) => u.is_active).length;
  const adminCount = items.filter((u) => u.is_active && u.role === 'org_admin').length;

  return (
    <div className="space-y-6" data-testid="users-page">
      <PageHeader eyebrow="Access control" title="Users & RBAC" />

      {/* Summary line */}
      {!isLoading && !hasError && (
        <div className="-mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 font-mono text-[13px] text-muted-foreground">
          <span>
            <span className="text-foreground">{items.length}</span>{' '}
            {items.length === 1 ? 'user' : 'users'}
            {isPaginated ? ' on this page' : ''}
          </span>
          <span aria-hidden="true" className="text-border">
            |
          </span>
          <span>
            <span className="text-foreground">{activeCount}</span> active ·{' '}
            <span className="text-foreground">{adminCount}</span> admin
          </span>
        </div>
      )}

      {/* Counts, pills, and filters above/below all read one loaded page — make
          that explicit once there's more than one, so IT doesn't read them as
          org-wide totals (e.g. mid-roster "Active" count understates the org). */}
      {!isLoading && !hasError && isPaginated && (
        <p className="-mt-2 text-xs text-muted-foreground" data-testid="users-page-scoped-note">
          Showing this page only — counts and filters reflect users loaded here, not the full
          roster.
        </p>
      )}

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_PILLS.map(({ value, label }) => {
          const pillCount = counts[value];
          const isSelected = status === value;
          return (
            <FilterPill key={value} selected={isSelected} onClick={() => setStatus(value)}>
              {label} <span className="tabular-nums">{pillCount}</span>
            </FilterPill>
          );
        })}

        {/* Search input */}
        <div className="relative sm:ml-auto">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={q}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name or email…"
            aria-label="Search users"
            className="pl-8 h-11 md:h-9 w-full sm:w-60"
          />
        </div>
      </div>

      {/* State ladder */}
      {hasError ? (
        <Alert variant="destructive" data-testid="users-error">
          <AlertTitle>Couldn&apos;t load users.</AlertTitle>
          <AlertDescription>
            Something went wrong fetching the user list. Retry in a moment.
          </AlertDescription>
        </Alert>
      ) : isLoading ? (
        <div className="space-y-3" data-testid="users-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="h-16 w-full rounded-gl bg-muted motion-safe:animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="No users have signed in yet."
          description="Users appear here after their first sign-in via your IdP."
        />
      ) : filtered.length === 0 ? (
        <EmptyState title="No users match." description="Try a different search or filter." />
      ) : (
        <>
          <div className="space-y-6">
            <UsersSection
              label="Administrators"
              note="Govern every app via org admin."
              users={admins}
              onInspect={openInspect}
            />
            <UsersSection label="Members" users={members} onInspect={openInspect} />
            <UsersSection
              label="Deactivated"
              note="Disabled in your IdP — all app access revoked."
              users={deactivated}
              onInspect={openInspect}
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={cursorStack.length === 0}
              data-testid="users-prev"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={goNext}
              disabled={!nextCursor}
              data-testid="users-next"
            >
              Next
            </Button>
          </div>
        </>
      )}

      {/* Always mounted; `selectedUser` persists through the close animation. */}
      <UserInspector
        user={selectedUser}
        open={inspectOpen}
        onOpenChange={setInspectOpen}
        roleSource={roleSource}
        onUserUpdated={(updated) => {
          setSelectedUser(updated);
          void queryClient.invalidateQueries({ queryKey: ['users'] });
        }}
      />

      {/* Groups — Greenlight groups and the registered directory groups app
           owners pick from (docs/41 §UI). Offered to every org; only the
           registry half depends on whether the IdP delivers groups at all. */}
      <GroupsSection canManage={isOrgAdmin} groupSource={groupSource} users={items} />

      {/* RBAC: org-level group → role mapping. Shown only in IdP-managed mode —
           under Greenlight management groups don't drive roles (docs/28 §/users).
           The editor is org_admin-only (it reads the org-admin-only identity). */}
      {roleSource === 'idp' && <RbacMatrix />}
    </div>
  );
}
